export const DEPOSIT_HISTORY_STORAGE_KEY = "websdk-sandbox-deposit-history";
export const DEPOSIT_HISTORY_CHANGED_EVENT =
  "websdk-sandbox-deposit-history-changed";

const MAX_DEPOSIT_HISTORY_ENTRIES = 50;
let memoryDepositHistory = [];
let forceMemoryDepositHistory = false;

const getStorage = () => {
  try {
    if (
      forceMemoryDepositHistory ||
      typeof window === "undefined" ||
      !window.localStorage
    ) {
      return null;
    }

    const storage = window.localStorage;
    const testKey = `${DEPOSIT_HISTORY_STORAGE_KEY}-test`;
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return storage;
  } catch {
    forceMemoryDepositHistory = true;
    return null;
  }
};

const notifyDepositHistoryChanged = (entries) => {
  if (typeof window === "undefined") {
    return;
  }

  if (typeof CustomEvent === "function") {
    window.dispatchEvent(
      new CustomEvent(DEPOSIT_HISTORY_CHANGED_EVENT, {
        detail: { entries }
      })
    );
    return;
  }

  const event = document.createEvent("CustomEvent");
  event.initCustomEvent(DEPOSIT_HISTORY_CHANGED_EVENT, false, false, {
    entries
  });
  window.dispatchEvent(event);
};

const SENSITIVE_KEY_PARTS = [
  "authorization",
  "card",
  "cvv",
  "holder",
  "name",
  "number",
  "pan",
  "token"
];

const maskCardLikeValue = (value) => {
  if (value == null) {
    return value;
  }

  const text = String(value);
  if (text.includes("*")) {
    return text;
  }

  const digits = text.replace(/\D/g, "");
  if (digits.length < 5) {
    return text;
  }

  return `****${digits.slice(-4)}`;
};

const sanitizeValue = (value, key = "") => {
  const normalizedKey = key.toLowerCase();

  if (value == null) {
    return value;
  }

  if (normalizedKey.includes("cvv")) {
    return "[redacted]";
  }

  if (
    SENSITIVE_KEY_PARTS.some((sensitiveKeyPart) =>
      normalizedKey.includes(sensitiveKeyPart)
    )
  ) {
    if (
      normalizedKey.includes("card") ||
      normalizedKey.includes("pan") ||
      normalizedKey.includes("number")
    ) {
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return {
          pan: maskCardLikeValue(value.pan),
          bin: value.bin,
          exp: value.exp
        };
      }

      return maskCardLikeValue(value);
    }

    return "[redacted]";
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeValue(item));
  }

  if (value && typeof value === "object") {
    return Object.entries(value).reduce((sanitized, [entryKey, entryValue]) => {
      sanitized[entryKey] = sanitizeValue(entryValue, entryKey);
      return sanitized;
    }, {});
  }

  return value;
};

const sanitizeSubmittedData = (data) => {
  if (!data || typeof data !== "object") {
    return data;
  }

  return Object.entries(data).reduce((sanitized, [key, value]) => {
    sanitized[key] = sanitizeValue(value, key);
    return sanitized;
  }, {});
};

export const formatDepositHistoryMessage = (value) => {
  if (value == null) {
    return undefined;
  }

  if (typeof value === "string") {
    return value === "[object Object]" ? undefined : value;
  }

  if (typeof value !== "object") {
    return String(value);
  }

  const nestedMessage =
    value.message ||
    value.errorMessage ||
    value.detail ||
    value.title ||
    value.reason ||
    value.description;
  const formattedNestedMessage = formatDepositHistoryMessage(nestedMessage);

  if (formattedNestedMessage) {
    return formattedNestedMessage;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const sanitizeError = (error) => {
  if (!error) {
    return null;
  }

  return {
    code: error.code || error.errorCode || error.cause?.code,
    message:
      formatDepositHistoryMessage(error.message) ||
      formatDepositHistoryMessage(error.errorMessage) ||
      formatDepositHistoryMessage(error.cause?.message) ||
      formatDepositHistoryMessage(error),
    name: error.name,
    status: error.status
  };
};

export const sanitizeDepositHistoryData = (entry = {}) => {
  const resultErrorCode = entry.result?.errorCode;
  const resultErrorMessage = formatDepositHistoryMessage(
    entry.result?.errorMessage
  );
  const errorCode = entry.errorCode || resultErrorCode;
  const errorMessage =
    formatDepositHistoryMessage(entry.errorMessage) || resultErrorMessage;

  return {
    attemptId: entry.attemptId,
    createdAt: entry.createdAt,
    updatedAt: entry.updatedAt,
    status: entry.status || "Pending",
    amount: entry.amount,
    currency: entry.currency,
    bonusCode: entry.bonusCode,
    paymentOption: entry.paymentOption
      ? {
          id: entry.paymentOption.id,
          displayName: entry.paymentOption.displayName,
          dataType: entry.paymentOption.dataType
        }
      : null,
    depositId: entry.depositId,
    requiredAction: entry.requiredAction,
    ctaUrl: entry.ctaUrl,
    cashTransferCode: entry.cashTransferCode,
    errorCode,
    errorMessage,
    depositFetchError: formatDepositHistoryMessage(entry.depositFetchError),
    data: sanitizeSubmittedData(entry.data),
    request: sanitizeSubmittedData(entry.request || entry.data),
    result: sanitizeSubmittedData(entry.result),
    deposit: entry.deposit,
    error:
      sanitizeError(entry.error) ||
      (errorMessage || errorCode
        ? { code: errorCode, message: errorMessage }
        : null)
  };
};

const normalizeEntries = (entries) => {
  const byIdentity = [];

  entries
    .map((entry) => sanitizeDepositHistoryData(entry))
    .filter((entry) => entry.attemptId || entry.depositId)
    .forEach((entry) => {
      const existingIndex = byIdentity.findIndex(
        (candidate) =>
          (entry.attemptId && candidate.attemptId === entry.attemptId) ||
          (entry.depositId && candidate.depositId === entry.depositId)
      );

      if (existingIndex >= 0) {
        byIdentity[existingIndex] = {
          ...byIdentity[existingIndex],
          ...entry,
          createdAt: byIdentity[existingIndex].createdAt || entry.createdAt
        };
      } else {
        byIdentity.push(entry);
      }
    });

  return byIdentity
    .sort(
      (first, second) =>
        new Date(second.updatedAt || second.createdAt || 0).getTime() -
        new Date(first.updatedAt || first.createdAt || 0).getTime()
    )
    .slice(0, MAX_DEPOSIT_HISTORY_ENTRIES);
};

export const loadDepositHistory = () => {
  const storage = getStorage();
  if (!storage) {
    return memoryDepositHistory;
  }

  try {
    const storedValue = storage.getItem(DEPOSIT_HISTORY_STORAGE_KEY);
    if (!storedValue) {
      return [];
    }

    const parsedValue = JSON.parse(storedValue);
    return Array.isArray(parsedValue) ? normalizeEntries(parsedValue) : [];
  } catch {
    return [];
  }
};

export const saveDepositHistory = (entries) => {
  const normalizedEntries = normalizeEntries(entries);
  const storage = getStorage();

  if (storage) {
    try {
      storage.setItem(
        DEPOSIT_HISTORY_STORAGE_KEY,
        JSON.stringify(normalizedEntries)
      );
    } catch {
      forceMemoryDepositHistory = true;
      memoryDepositHistory = normalizedEntries;
    }
  } else {
    memoryDepositHistory = normalizedEntries;
  }

  notifyDepositHistoryChanged(normalizedEntries);
  return normalizedEntries;
};

export const upsertDepositHistoryEntry = (entry) => {
  const now = new Date().toISOString();
  const entries = loadDepositHistory();
  const existingEntry = entries.find(
    (candidate) =>
      (entry.attemptId && candidate.attemptId === entry.attemptId) ||
      (entry.depositId && candidate.depositId === entry.depositId)
  );

  return saveDepositHistory([
    {
      ...existingEntry,
      ...entry,
      createdAt: existingEntry?.createdAt || entry.createdAt || now,
      updatedAt: entry.updatedAt || now
    },
    ...entries.filter(
      (candidate) =>
        !(entry.attemptId && candidate.attemptId === entry.attemptId) &&
        !(entry.depositId && candidate.depositId === entry.depositId)
    )
  ]);
};

export const clearDepositHistory = () => {
  const storage = getStorage();
  memoryDepositHistory = [];

  if (storage) {
    try {
      storage.removeItem(DEPOSIT_HISTORY_STORAGE_KEY);
    } catch {
      // Ignore storage failures; memory fallback has already been cleared.
    }
  }

  notifyDepositHistoryChanged([]);
};
